import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Flame, Calendar, Trophy, Target } from 'lucide-react';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  totalCheckIns: number;
  last30Days: number;
  badges: string[];
  graceActive: boolean;
}

export function CheckInStreaks() {
  const [streakData, setStreakData] = useState<StreakData>({
    currentStreak: 0,
    longestStreak: 0,
    totalCheckIns: 0,
    last30Days: 0,
    badges: [],
    graceActive: false,
  });

  useEffect(() => {
    calculateStreaks();
  }, []);

  const calculateStreaks = () => {
    const activeRelationshipId = localStorage.getItem('tension_active_relationship');
    const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    
    // Filter for active relationship
    const checkins = activeRelationshipId
      ? allCheckins.filter((c: any) => c.relationshipId === activeRelationshipId)
      : allCheckins;

    if (checkins.length === 0) {
      return;
    }

    // Sort by timestamp
    const sorted = checkins.sort((a: any, b: any) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Calculate current streak
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 1;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if there's a check-in today or yesterday
    const lastCheckin = new Date(sorted[sorted.length - 1].timestamp);
    lastCheckin.setHours(0, 0, 0, 0);
    
    const daysSinceLastCheckin = Math.floor((today.getTime() - lastCheckin.getTime()) / (1000 * 60 * 60 * 24));
    const graceActive = daysSinceLastCheckin === 2;
    
    if (daysSinceLastCheckin <= 2) {
      // Count backwards to find current streak
      currentStreak = 1;
      for (let i = sorted.length - 2; i >= 0; i--) {
        const current = new Date(sorted[i + 1].timestamp);
        current.setHours(0, 0, 0, 0);
        const previous = new Date(sorted[i].timestamp);
        previous.setHours(0, 0, 0, 0);
        
        const daysDiff = Math.floor((current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 1) {
          currentStreak++;
        } else if (daysDiff === 0) {
          // Same day, don't increment
          continue;
        } else {
          break;
        }
      }
    }

    // Calculate longest streak
    for (let i = 1; i < sorted.length; i++) {
      const current = new Date(sorted[i].timestamp);
      current.setHours(0, 0, 0, 0);
      const previous = new Date(sorted[i - 1].timestamp);
      previous.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.floor((current.getTime() - previous.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 1) {
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else if (daysDiff === 0) {
        // Same day, don't break streak
        continue;
      } else {
        tempStreak = 1;
      }
    }
    
    longestStreak = Math.max(longestStreak, tempStreak, currentStreak);

    // Last 30 days count
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const last30Days = checkins.filter((c: any) => 
      new Date(c.timestamp) >= thirtyDaysAgo
    ).length;

    // Badge logic
    const badges: string[] = [];
    if (currentStreak >= 7) badges.push('7-day');
    if (currentStreak >= 14) badges.push('14-day');
    if (currentStreak >= 30) badges.push('30-day');
    if (currentStreak >= 60) badges.push('60-day');
    if (currentStreak >= 90) badges.push('90-day');
    if (longestStreak >= 30) badges.push('marathon');
    if (checkins.length >= 100) badges.push('centurion');

    setStreakData({
      currentStreak,
      longestStreak,
      totalCheckIns: checkins.length,
      last30Days,
      badges,
      graceActive,
    });
  };

  const getStreakEmoji = () => {
    if (streakData.currentStreak === 0) return '💤';
    if (streakData.currentStreak >= 30) return '🔥🔥🔥';
    if (streakData.currentStreak >= 14) return '🔥🔥';
    if (streakData.currentStreak >= 7) return '🔥';
    return '✨';
  };

  const getMotivationalMessage = () => {
    if (streakData.currentStreak === 0) return 'Start your streak today!';
    if (streakData.currentStreak === 1) return 'Great start! Keep it going.';
    if (streakData.currentStreak >= 7 && streakData.currentStreak < 14) return 'One week strong! 🎉';
    if (streakData.currentStreak >= 14 && streakData.currentStreak < 30) return 'Two weeks! Consistency builds clarity.';
    if (streakData.currentStreak >= 30) return 'Incredible dedication! 30+ days! 🏆';
    return `${streakData.currentStreak} days of self-awareness!`;
  };

  const nextMilestone = () => {
    if (streakData.currentStreak < 7) return { days: 7 - streakData.currentStreak, name: '7-day badge' };
    if (streakData.currentStreak < 14) return { days: 14 - streakData.currentStreak, name: '14-day badge' };
    if (streakData.currentStreak < 30) return { days: 30 - streakData.currentStreak, name: '30-day badge' };
    if (streakData.currentStreak < 60) return { days: 60 - streakData.currentStreak, name: '60-day badge' };
    if (streakData.currentStreak < 90) return { days: 90 - streakData.currentStreak, name: '90-day badge' };
    return null;
  };

  const milestone = nextMilestone();

  return (
    <Card className="p-6 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
      <div className="space-y-4">
        {/* Main Streak Display */}
        <div className="text-center">
          <div className="text-5xl mb-2">{getStreakEmoji()}</div>
          <div className="text-4xl font-light text-neutral-900">
            {streakData.currentStreak}
          </div>
          <p className="text-sm text-neutral-600">Day Streak</p>
          <p className="text-xs font-medium text-orange-600 mt-1">
            {getMotivationalMessage()}
          </p>
          {streakData.graceActive && (
            <p className="text-xs text-amber-700 mt-1">
              Grace day is active. A check-in today keeps your streak intact.
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/60 rounded-lg p-3 text-center">
            <Trophy className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <div className="text-xl font-light text-neutral-900">{streakData.longestStreak}</div>
            <div className="text-xs text-neutral-600">Longest</div>
          </div>
          <div className="bg-white/60 rounded-lg p-3 text-center">
            <Calendar className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <div className="text-xl font-light text-neutral-900">{streakData.last30Days}/30</div>
            <div className="text-xs text-neutral-600">This Month</div>
          </div>
          <div className="bg-white/60 rounded-lg p-3 text-center">
            <Target className="w-4 h-4 text-green-500 mx-auto mb-1" />
            <div className="text-xl font-light text-neutral-900">{streakData.totalCheckIns}</div>
            <div className="text-xs text-neutral-600">Total</div>
          </div>
        </div>

        {/* Next Milestone */}
        {milestone && (
          <div className="bg-white/60 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700">Next milestone</span>
              <span className="text-sm font-medium text-neutral-900">
                {milestone.days} {milestone.days === 1 ? 'day' : 'days'} to {milestone.name}
              </span>
            </div>
            <div className="mt-2 bg-neutral-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-orange-400 to-red-400 h-2 rounded-full transition-all"
                style={{
                  width: `${(streakData.currentStreak / (streakData.currentStreak + milestone.days)) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Badges */}
        {streakData.badges.length > 0 && (
          <div className="bg-white/60 rounded-lg p-3">
            <p className="text-xs font-medium text-neutral-700 mb-2">Earned Badges</p>
            <div className="flex flex-wrap gap-2">
              {streakData.badges.map((badge) => (
                <div
                  key={badge}
                  className="px-2 py-1 bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 rounded text-xs font-medium text-amber-800"
                >
                  {badge === '7-day' && '🔥 Week Warrior'}
                  {badge === '14-day' && '🔥🔥 Two-Week Champion'}
                  {badge === '30-day' && '🏆 Monthly Master'}
                  {badge === '60-day' && '💎 60-Day Diamond'}
                  {badge === '90-day' && '👑 90-Day Royalty'}
                  {badge === 'marathon' && '🏃 Marathon Runner'}
                  {badge === 'centurion' && '💯 Centurion'}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white/60 rounded-lg p-3">
          <p className="text-xs text-neutral-600 text-center">
            Consistency score: {streakData.last30Days > 0 ? Math.round((streakData.last30Days / 30) * 100) : 0}%
            {streakData.last30Days >= 25 && ' - Exceptional! 🌟'}
            {streakData.last30Days >= 20 && streakData.last30Days < 25 && ' - Great! 💪'}
            {streakData.last30Days >= 15 && streakData.last30Days < 20 && ' - Good progress'}
            {streakData.last30Days < 15 && ' - Keep building the habit'}
          </p>
        </div>
      </div>
    </Card>
  );
}
